/**
 * Панель инструмента маршрутизации OSRM: A→B и ближайшие по дороге.
 */

import { Button } from "@/components/ui/button";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { cn } from "@/lib/utils";
import type { RouteToolMode } from "@/components/map-libre/MapLibreRouteTool";

export type NearestRankRow = {
  index: number;
  duration_s: number | null;
  distance_m: number | null;
};

type Props = {
  mode: RouteToolMode;
  onModeChange: (mode: RouteToolMode) => void;
  pointCount: number;
  loading?: boolean;
  routeSummary?: {
    distance_m?: number | null;
    duration_s?: number | null;
    air_distance_m?: number | null;
  } | null;
  nearestRows?: NearestRankRow[];
  error?: string | null;
  onClear: () => void;
  onComputeNearest: () => void;
  onClose: () => void;
  className?: string;
};

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "—";
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} мин`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h} ч ${rem} мин` : `${h} ч`;
}

function formatDistance(meters: number): string {
  if (!Number.isFinite(meters) || meters < 0) return "—";
  if (meters < 1000) return `${Math.round(meters)} м`;
  return `${(meters / 1000).toFixed(1)} км`;
}

export function RoutingToolPanel({
  mode,
  onModeChange,
  pointCount,
  loading = false,
  routeSummary = null,
  nearestRows = [],
  error = null,
  onClear,
  onComputeNearest,
  onClose,
  className,
}: Props) {
  const hint =
    mode === "route"
      ? pointCount === 0
        ? "Кликните точку A на карте"
        : pointCount === 1
          ? "Кликните точку B"
          : "Маршрут построен. Новый клик — начать заново"
      : pointCount === 0
        ? "Кликните исходную точку"
        : "Добавляйте цели кликами, затем «Рассчитать»";

  return (
    <div
      className={cn(
        "absolute bottom-4 left-1/2 z-[210] w-[min(24rem,calc(100%-2rem))] -translate-x-1/2",
        "rounded-md border border-border bg-background/95 p-3 shadow-md",
        className,
      )}
      role="region"
      aria-label="Маршрутизация"
    >
      <div className="flex items-center gap-1.5">
        <h3 className="text-sm font-medium text-foreground">Маршрут по дорогам</h3>
        <SectionHelpTooltip label="Справка по маршрутизации">
          <p>
            Расчёт автомобильного маршрута и ближайших точек по дорожной сети (OSRM), не по прямой.
          </p>
          <ul className="list-disc space-y-1 pl-4">
            <li>
              <strong>A→B</strong> — два клика по карте: линия, длина и время.
            </li>
            <li>
              <strong>Ближайшие</strong> — клик origin, затем цели; «Рассчитать» ранжирует по
              времени/км по дороге.
            </li>
            <li>Кнопка «Снять» очищает оверлей.</li>
          </ul>
          <p>
            <strong>Клавиатура:</strong> Esc — выйти и снять маршрут.
          </p>
        </SectionHelpTooltip>
        <div className="ml-auto flex gap-1">
          <Button
            type="button"
            size="sm"
            variant={mode === "route" ? "secondary" : "ghost"}
            className="h-7 px-2 text-xs"
            onClick={() => onModeChange("route")}
          >
            A→B
          </Button>
          <Button
            type="button"
            size="sm"
            variant={mode === "nearest" ? "secondary" : "ghost"}
            className="h-7 px-2 text-xs"
            onClick={() => onModeChange("nearest")}
          >
            Ближайшие
          </Button>
        </div>
      </div>

      <p className="mt-2 text-xs text-muted-foreground">{hint}</p>
      <p className="mt-0.5 text-[11px] text-muted-foreground tabular-nums">
        Точек: {pointCount}
        {loading ? " · расчёт…" : ""}
      </p>

      {error ? (
        <p className="mt-2 text-xs text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      {routeSummary ? (
        <div className="mt-2 space-y-0.5 text-sm tabular-nums text-foreground">
          {routeSummary.distance_m != null && routeSummary.duration_s != null ? (
            <p>
              По дороге: {formatDistance(routeSummary.distance_m)} ·{" "}
              {formatDuration(routeSummary.duration_s)}
            </p>
          ) : null}
          {routeSummary.air_distance_m != null ? (
            <p className="text-xs text-muted-foreground">
              По воздуху: {formatDistance(routeSummary.air_distance_m)}
            </p>
          ) : null}
        </div>
      ) : null}

      {nearestRows.length > 0 ? (
        <ol className="mt-2 max-h-32 space-y-1 overflow-y-auto text-xs tabular-nums">
          {nearestRows.map((row, rank) => (
            <li key={row.index} className="flex justify-between gap-2 border-b border-border/60 py-0.5">
              <span>
                #{rank + 1} · цель {row.index}
              </span>
              <span className="text-muted-foreground">
                {row.distance_m != null ? formatDistance(row.distance_m) : "—"} ·{" "}
                {row.duration_s != null ? formatDuration(row.duration_s) : "—"}
              </span>
            </li>
          ))}
        </ol>
      ) : null}

      <div className="mt-3 flex flex-wrap gap-2">
        {mode === "nearest" ? (
          <Button
            type="button"
            size="sm"
            className="h-7"
            disabled={loading || pointCount < 2}
            onClick={onComputeNearest}
          >
            Рассчитать
          </Button>
        ) : null}
        <Button type="button" size="sm" variant="outline" className="h-7" onClick={onClear}>
          Снять
        </Button>
        <Button type="button" size="sm" variant="ghost" className="h-7" onClick={onClose}>
          Закрыть
        </Button>
      </div>
    </div>
  );
}
